"use client";

import { useCallback, useState, useTransition } from "react";

import Link from "next/link";

import { zodResolver } from "@hookform/resolvers/zod";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Tables } from "@kit/supabase/database";
import { Button } from "@kit/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@kit/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@kit/ui/form";
import { Heading } from "@kit/ui/heading";
import { If } from "@kit/ui/if";
import { LoadingOverlay } from "@kit/ui/loading-overlay";
import { Textarea } from "@kit/ui/textarea";
import { Trans } from "@kit/ui/trans";

import { insertIssueCommentAction } from "../lib/server/server-actions";
import { IssueStatusBadge } from "./issue-status-badge";
import { IssueTypeBadge } from "./issue-type-badge";
import { IssueVotesBadge } from "./issue-votes-badge";

export function IssueDetailDialog(
  props: React.PropsWithChildren<{
    issue: Tables<"issues">;
    canComment: boolean;
    className?: string;
    isOpen?: boolean;
    onClose?: () => void;
  }>,
) {
  const [isOpen, setIsOpen] = useState(props.isOpen ?? false);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);

        if (!open && props.onClose) {
          props.onClose();
        }
      }}
    >
      <DialogTrigger className={props.className} asChild>
        {props.children}
      </DialogTrigger>

      <DialogContent className={"lg:min-w-[800px]"}>
        <DialogHeader>
          <DialogTitle>{props.issue.title}</DialogTitle>

          <DialogDescription>{props.issue.description}</DialogDescription>
        </DialogHeader>

        <FeatureRequestDetail
          canComment={props.canComment}
          issue={props.issue}
        />
      </DialogContent>
    </Dialog>
  );
}

function FeatureRequestDetail({
  issue,
  canComment,
}: React.PropsWithChildren<{
  issue: Tables<"issues">;
  canComment: boolean;
}>) {
  return (
    <div className={"flex flex-col space-y-4"}>
      <div className={"flex gap-2"}>
        <IssueVotesBadge votes={issue.votes} />
        <IssueTypeBadge type={issue.type} />
        <IssueStatusBadge status={issue.status} />
      </div>

      <IssueDetailComments canComment={canComment} issueId={issue.id} />
    </div>
  );
}

function IssueDetailComments(props: { issueId: string; canComment: boolean }) {
  const [page, setPage] = useState(1);
  const appendNewComment = useAppendNewComment(page);

  const { data: comments, isLoading } = useFetchIssueComments({
    issueId: props.issueId,
    page,
  });

  const pages = comments?.pages ?? [];
  const isEmpty = pages.length === 0;

  if (isLoading) {
    return (
      <LoadingOverlay
        className={
          "animate-in fade-in zoom-in-95 slide-in-from-bottom-20 duration-500"
        }
        fullPage={false}
      >
        <span className={"text-muted-foreground text-sm"}>
          <Trans
            i18nKey={"roadmap:loadingComments"}
            defaults={"Loading comments..."}
          />
        </span>
      </LoadingOverlay>
    );
  }

  if (isEmpty) {
    return (
      <div className={"flex flex-col gap-4"}>
        <p className={"text-muted-foreground text-sm"}>
          <Trans
            i18nKey={"roadmap:noComments"}
            defaults={
              "There are no comments on this feature request yet. Be the first to comment!"
            }
          />
        </p>

        <CommentForm
          disabled={!props.canComment}
          issueId={props.issueId}
          onSubmit={appendNewComment}
        />
      </div>
    );
  }

  if (!comments) {
    return null;
  }

  const totalComments = comments.pages.reduce(
    (acc, page) => acc + page.comments.length,
    0,
  );

  const totalCount = comments?.pages[0]!.count;
  const hasMore = totalCount > totalComments;

  const commentsCount = comments?.pages[0]!.count ?? 0;

  return (
    <div className={"flex flex-col gap-2.5"}>
      <CommentForm
        disabled={!props.canComment}
        issueId={props.issueId}
        onSubmit={appendNewComment}
      />

      <Heading level={6}>
        <span className={"text-sm"}>
          <Trans
            i18nKey={"roadmap:comments"}
            defaults={`Comments (${commentsCount})`}
            values={{ count: commentsCount }}
          />
        </span>
      </Heading>

      <div className={"flex max-h-[40vh] flex-col gap-2 overflow-y-auto"}>
        {pages.map((page, index) => (
          <div className={"flex flex-col gap-2"} key={index + page.count}>
            {page.comments.map((comment) => (
              <div
                key={comment.id}
                className={
                  "border-border/50 flex flex-col gap-1 rounded-lg border p-4"
                }
              >
                <div className={"text-muted-foreground text-xs"}>
                  <Trans
                    i18nKey={"roadmap:writtenOn"}
                    values={{
                      date: new Date(comment.created_at).toLocaleString(),
                    }}
                  />
                </div>

                <div className="text-sm">{comment.content}</div>
              </div>
            ))}
          </div>
        ))}

        <If condition={hasMore}>
          <div className={"flex justify-center"}>
            <Button
              variant={"link"}
              size={"sm"}
              onClick={() => setPage((prev) => prev + 1)}
            >
              <Trans
                i18nKey={"roadmap:loadMoreComments"}
                defaults={"Load More Comments"}
              />
            </Button>
          </div>
        </If>
      </div>
    </div>
  );
}

function useFetchIssueComments(params: { issueId: string; page: number }) {
  return useInfiniteQuery({
    queryKey: ["issue_comments", params.issueId, params.page],
    initialPageParam: params.page,
    getNextPageParam: () => params.page + 1,
    getPreviousPageParam: () => params.page - 1,
    queryFn: async ({ pageParam = 1 }) => {
      const searchParams = new URLSearchParams({
        page: pageParam.toString(),
        issueId: params.issueId,
      });

      const url = `/roadmap/comments?${searchParams.toString()}`;

      const response = await fetch(url);

      return (await response.json()) as {
        comments: Tables<"issue_comments">[];
        count: number;
      };
    },
  });
}

function CommentForm(props: {
  issueId: string;
  onSubmit: (data: Tables<"issue_comments">) => void;
  disabled: boolean;
}) {
  const [pending, startTransition] = useTransition();

  const form = useForm({
    resolver: zodResolver(
      z.object({
        content: z.string().min(20).max(5000),
      }),
    ),
    defaultValues: {
      content: "",
    },
  });

  return (
    <Form {...form}>
      <form
        className={"flex flex-col space-y-2"}
        onSubmit={form.handleSubmit((data) => {
          startTransition(async () => {
            const { data: comment } = await insertIssueCommentAction({
              content: data.content,
              issueId: props.issueId,
            });

            form.reset();

            props.onSubmit(comment);
          });
        })}
      >
        <FormField
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    disabled={props.disabled}
                    required
                    placeholder={"Comment on this feature..."}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
          name={"content"}
        />

        <If
          condition={!props.disabled}
          fallback={
            <span className={"text-muted-foreground text-sm"}>
              Please <Link href="/auth/sign-in">sign in</Link> to leave a
              comment
            </span>
          }
        >
          <div className={"flex justify-end"}>
            <Button disabled={pending} size={"sm"}>
              <Trans
                i18nKey={"roadmap:submitComment"}
                defaults={"Submit Comment"}
              />
            </Button>
          </div>
        </If>
      </form>
    </Form>
  );
}

function useAppendNewComment(page: number) {
  const queryClient = useQueryClient();

  return useCallback(
    (comment: Tables<"issue_comments">) => {
      queryClient.setQueryData(
        ["issue_comments", comment.issue_id, page],
        (
          data:
            | {
                pages: {
                  comments: Tables<"issue_comments">[];
                  count: number;
                }[];
              }
            | undefined,
        ) => {
          if (!data) {
            return {
              pages: [
                {
                  comments: [comment],
                  count: 1,
                },
              ],
            };
          }

          const pages = data.pages;

          if (!pages.length || !pages[0]) {
            return {
              pages: [
                {
                  comments: [comment],
                  count: 1,
                },
              ],
            };
          }

          const firstPage = {
            comments: [comment, ...pages[0].comments],
            count: pages[0].count + 1,
          };

          const otherPages = data.pages.slice(1);

          return {
            ...data,
            pages: [firstPage, ...otherPages],
          };
        },
      );
    },
    [queryClient, page],
  );
}
